import { FormSchema, Lookups } from '@precert/validation';
import { useTableNav } from '@table-nav/react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';
import cx from 'classnames';
import mime from 'mime';
import React, { useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Spinner } from 'reactstrap';

import { useConfig } from '../../../../_components/config';
import SingleErrorDisplay from '../../../../_components/error-display/single-error-display';
import { useFormFieldContext } from '../../../../_components/form-hook';
import { useToastr } from '../../../../_components/toastr';

import styles from './step-clinical-documents.module.scss';
import { useUploadFile } from './upload-file';

export interface StepAttachmentsProps {
  index: number;
}

export interface AttachmentFormData {
  fileName: string;
  contentType: string;
  contentLength: number;
  attachmentId?: string;
  uploaded?: boolean;
}

export interface StepAttachmentsFormData {
  attachments: AttachmentFormData[];
}

const { fileTypes } = Lookups;

export const accept = fileTypes.reduce(
  (acc, val) => {
    const ext = `.${val.toLowerCase()}`;
    const type = mime.getType(ext) ?? 'application/octet-stream';
    const arr = acc[type] ?? [];
    if (arr.indexOf(ext) !== 0) {
      arr.push(ext);
    }
    return {
      ...acc,
      [type]: arr,
    };
  },
  {} as Record<string, string[]>
);

export const attachmentsErrorMessage =
  'At least one clinical document is required.';

export const StepAttachmentsSchema = FormSchema()
  .pick(['attachments'])
  .test('uploaded', 'Not all files are uploaded', (files) => {
    const uploaded = files?.attachments
      ? files?.attachments.every((m) => m.uploaded)
      : false;
    return uploaded;
  });

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 bytes';
  const k = 1024;
  const dm = Math.max(decimals, 0);
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

export const StepAttachments = ({ isUpdate }: { isUpdate?: boolean }) => {
  const { listeners } = useTableNav();
  const columnHelper = createColumnHelper<AttachmentFormData>();

  const { maxFileSizeMegaBytes } = useConfig();
  if (!maxFileSizeMegaBytes) {
    throw new Error('The maximum file size is not configured');
  }
  const maxFileSizeBytes = maxFileSizeMegaBytes * 1024 ** 2;

  const { value, setValue } =
    useFormFieldContext<AttachmentFormData[]>('attachments');
  const attachments = value ?? [];
  const toast = useToastr();
  const { onDrop } = useUploadFile();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    accept,
    maxSize: maxFileSizeBytes,
  });

  // TODO CCRA-37242
  useEffect(() => {
    if (fileRejections.length > 0) {
      for (const fileRejection of fileRejections) {
        const { errors, file } = fileRejection;
        const [{ code }] = errors;
        let [{ message }] = errors;
        if (code === 'file-too-large') {
          message = `File is larger than ${formatBytes(maxFileSizeBytes)}`;
        } else if (code === 'file-invalid-type') {
          message = 'File type is not accepted';
        }
        toast.error(
          <span>
            <b>Error uploading {file.name}</b>
            <br />
            {message}
          </span>
        );
      }
    }
  }, [fileRejections]);
  const removeAttachment = (i: number) => {
    attachments.splice(i, 1);
    setValue(attachments, { shouldValidate: true });
  };
  const columns = [
    columnHelper.accessor('fileName', {
      header: 'File Name',
      id: 'fileName',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('contentLength', {
      header: 'File Length',
      id: 'fileLength',

      cell: (info) => formatBytes(info.getValue()),
    }),
    columnHelper.accessor('fileName', {
      header: 'File Type',
      id: 'extension',
      cell: (info) => info.getValue().match(/\w+$/),
    }),
    columnHelper.accessor('uploaded', {
      header: 'Remove',
      id: 'remove',
      meta: {
        ariaDescription: 'Press enter to enter this cell to remove this file',
      },
      cell: (info) => {
        const fileName: string = info.row.original.fileName;
        const row = +info.row.id;
        return info ? (
          <Button
            tabIndex={-1}
            aria-description={`Press Enter to remove ${fileName} from the list of Clinical Documents`}
            size="sm"
            onClick={() => {
              removeAttachment(row);
              toast.success(<span>{fileName} removed</span>);
            }}
            style={{ backgroundColor: '#af0e29' }}
            aria-label={`Remove File ${fileName}`}
          >
            X
          </Button>
        ) : (
          <Spinner size="sm" className="ms-2" />
        );
      },
    }),
  ];
  // TODO CCRA-37242
  const table = useReactTable({
    columns: columns,
    data: attachments,
    getCoreRowModel: getCoreRowModel(),
  });

  // TODO CCRA-37242
  const children = useMemo(() => {
    if (attachments && attachments.length > 0) {
      return (
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  role="gridcell"
                  tabIndex={0}
                  key={cell.id}
                  aria-description={
                    cell?.column?.columnDef.meta
                      ? cell?.column?.columnDef.meta.ariaDescription
                      : ''
                  }
                  className={cx(styles.gridRow, {
                    [styles.isOddRow]: row.index % 2 === 1,
                    [styles.isEvenRow]: row.index % 2 === 0,
                  })}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      );
    }
  }, [attachments]);
  const acceptedFileTypesText = `Accepted File Types : ${fileTypes.join(', ')}`;
  return (
    <>
      <div className="d-flex justify-content-center">
        <div
          className={cx(styles.dropTarget, {
            [styles.dropTargetActive]: isDragActive && !isDragReject,
            [styles.dropTargetRejected]: isDragReject,
          })}
          {...getRootProps()}
          role="button"
          aria-label={`Drag and drop files here or click to upload. ${acceptedFileTypesText}`}
          aria-details={acceptedFileTypesText}
        >
          <input {...getInputProps()} />
          <div className={styles.header}>
            Drag and drop files here, or <strong>click to upload</strong>.
          </div>
          <div className={cx('text-center', styles.acceptedFileTypes)}>
            {acceptedFileTypesText}
          </div>
        </div>
      </div>
      {isUpdate ? null : (
        <div
          className={`${styles.instructions} text-center pb-4 col-md-9 mx-auto`}
        >
          * Please submit supporting medical documentation (such as medical
          records, progress notes, lab reports, and/or letter of medical
          necessity) that provides clinical justification related to the care
          that will be delivered. At least one clinical document is required.
        </div>
      )}
      <table className="table" {...listeners}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  tabIndex={0}
                  colSpan={header.colSpan}
                  className={styles.gridHeader}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {children || null}
      </table>
      {!children && (
        <p className={cx('text-center', styles.emptyGrid)}>No files uploaded</p>
      )}
      <SingleErrorDisplay field="attachments" />
    </>
  );
};

export const stepAttachments = {
  title: 'Clinical Documents',
  schema: StepAttachmentsSchema,
  element: <StepAttachments isUpdate={false} />,
  path: 'attachments',
};
